import { CUSTOM_ELEMENTS_SCHEMA, isDevMode, NgModule, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { provideTranslateService, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { META_REDUCERS, provideStore, StoreRootModule } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { CanteenModule } from './pages/home/canteen/canteen.module';
import { DepartureMonitorPageModule } from './pages/home/departure-monitor/departure-monitor.module';

import { SettingsPage } from './pages/settings/settings.page';
import { ChangelogPage } from './pages/changelog/changelog.page';
import { HomescreenPage } from './pages/homescreen/homescreen.page';
import { PushNotificationPage } from './pages/push-notification/push-notification.page';
import { WizardPageModule } from './pages/wizard/wizard.module';
import { AccessibilityDisclaimerPage } from './pages/accessibility-disclaimer/accessibility-disclaimer.page';
import { LicensesComponent } from './pages/licenses/licenses.component';
import { LicenseDetailsComponent } from './pages/licenses/details/license-details.component';

import { settingsFeature } from './ngrx/settings/settings.feature';
import * as newsFeature from './ngrx/news/news.reducer';
import * as tileFeature from './ngrx/tile/tile.reducer';
import * as pushNotificationFeature from './ngrx/push-notification/push-notification.reducer';
import * as campusIdFeature from './ngrx/campus-id/campus-id.reducer';
import * as weatherFeature from './ngrx/weather/weather.reducer';

import { StatisticsService } from './services/statistics.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { PWAService } from './services/pwa.service';
import { LocationLookupPage } from './pages/location-lookup/location-lookup.page';
import { ComponentsModule } from './components/components.module';
import { NoContentComponent } from "src/app/components/no-content/no-content.component";
import { NgxMapLibreGLModule } from "@maplibre/ngx-maplibre-gl";

import { ConsoleLoggerFactory, LOGGER } from './oidc/shared/logger';
import { authInterceptor } from './oidc/auth.interceptor';
import { gateInterceptor } from './oidc/shared/gate.interceptor';
import { PreferencesTokenStorage, TOKEN_STORAGE } from './oidc/token-storage';
import { provideAuthInit } from './oidc/auth-init';
import { StorageEffects } from './oidc/store/storage.effects';
import { PreferencesStateStorage, STATE_STORAGE } from './oidc/store/state-storage';
import { hydrationMetaReducer } from './oidc/store/hydration.meta-reducer';
import { PERSISTED_SLICES } from './oidc/store/persisted-state';
import { provideSettingsPersistence } from './ngrx/settings/settings.persistence';
import { provideStorageInit } from './oidc/store/storage-init';

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
registerLocaleData(localeDe);

import './helper-functions';
import { CampusIdEffects } from './ngrx/campus-id/campus-id.effects';
import { NewsEffects } from './ngrx/news/news.effects';
import { PushNotificationEffects } from './ngrx/push-notification/push-notification.effects';
import { TileEffects } from './ngrx/tile/tile.effects';
import { WeatherEffects } from './ngrx/weather/weather.effects';
import { SettingsState } from './ngrx/settings/settings.models';
import { storageFeature } from './oidc/store/storage.feature';
import { SettingsEffects } from './ngrx/settings/settings.effects';
import { provideNewsPersistence } from './ngrx/news/news.persistence';
import { provideCampusIdPersistence } from './ngrx/campus-id/campus-id.persistance';
import { providePushNotificationPersistence } from './ngrx/push-notification/push-notification.persistance';
import { provideTilePersistence } from './ngrx/tile/tile.persistance';

export interface AppState {
  [settingsFeature.name]: SettingsState;
  [newsFeature.name]: newsFeature.NewsState;
  [tileFeature.name]: tileFeature.TilesState;
  [pushNotificationFeature.name]: pushNotificationFeature.PushNotificationState;
  [campusIdFeature.name]: campusIdFeature.CampusIdState;
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        BrowserModule,
        IonicModule.forRoot({ animated: true, innerHTMLTemplatesEnabled: true }),
        TranslatePipe,
        TranslateDirective,
        AppRoutingModule,
        CanteenModule,
        DepartureMonitorPageModule,
        WizardPageModule,
        ComponentsModule,
        NgxMapLibreGLModule,
        NoContentComponent,
        SettingsPage,
        ChangelogPage,
        HomescreenPage,
        PushNotificationPage,
        LicensesComponent,
        LicenseDetailsComponent,
        AccessibilityDisclaimerPage,
        LocationLookupPage,
        StoreRootModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode() && !Capacitor.isNativePlatform(),
            registrationStrategy: 'registerImmediately',
        }),
    ],
    providers: [
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
      { provide: LOGGER, useFactory: () => new ConsoleLoggerFactory(environment.production ? 'warn' : 'debug') },
      provideHttpClient(withInterceptors([gateInterceptor, authInterceptor])),
      provideTranslateService({
        loader: provideTranslateHttpLoader({
          prefix: './assets/i18n/',
          suffix: '.json',
        }),
      }),
      // @TODO: mabye provide a token storage using https://github.com/martinkasa/capacitor-secure-storage-plugin
      { provide: TOKEN_STORAGE, useClass: PreferencesTokenStorage },
      provideAuthInit(),
      provideStore({
        [storageFeature.name]: storageFeature.reducer,
        [settingsFeature.name]: settingsFeature.reducer,
        [newsFeature.name]: newsFeature.reducer,
        [tileFeature.name]: tileFeature.reducer,
        [pushNotificationFeature.name]: pushNotificationFeature.reducer,
        [campusIdFeature.name]: campusIdFeature.reducer,
        [weatherFeature.name]: weatherFeature.reducer,
      }, {
        runtimeChecks: {
          strictStateSerializability: true,
          strictActionSerializability: true,
          strictActionTypeUniqueness: true,
        },
      }),
      provideEffects(
        StorageEffects,
        CampusIdEffects,
        NewsEffects,
        PushNotificationEffects,
        SettingsEffects, // @TODO merge old and new settings
        TileEffects,
        WeatherEffects
      ),
      // @TODO: individual slices could move to an encrypted backend behind this same token
      { provide: STATE_STORAGE, useClass: PreferencesStateStorage },
      // Fills the registered slices from storage on `hydrated`. Needs the registry, so it is
      // provided through META_REDUCERS rather than passed to `provideStore` directly.
      {
        provide: META_REDUCERS,
        multi: true,
        useFactory: hydrationMetaReducer,
        deps: [PERSISTED_SLICES],
      },
      // Each feature contributes its own entry; adding a slice touches nothing here but the
      // reducer map above.
      provideSettingsPersistence(),
      provideNewsPersistence(),
      provideCampusIdPersistence(),
      providePushNotificationPersistence(),
      provideTilePersistence(),
      provideStorageInit(),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: environment.production,
        connectInZone: true,
      }),
      StatisticsService,
    ]
})
export class AppModule {
  private pwaService = inject(PWAService);
}
